<template>
  <Transition name="staff-grant-toast">
    <output v-if="copy" class="staff-grant-toast">
      <CoinToken v-if="coins > 0" size="md" />
      <p class="copy">{{ copy }}</p>
    </output>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onUnmounted, watch } from 'vue'
import CoinToken from './CoinToken.vue'
import { useI18n } from '../i18n/t'
import {
  ackStaffGrantNotice,
  staffGrantNoticeCoins,
  staffGrantNoticePacks,
} from '../account/staffGrantNotice'

const TOAST_MS = 8_000

const { t } = useI18n()
const coins = staffGrantNoticeCoins
const packs = staffGrantNoticePacks
const copy = computed(() => {
  const nCoins = coins.value
  const nPacks = packs.value
  if (nCoins > 0 && nPacks > 0) {
    return t('staffGrant.toastBoth', { coins: nCoins, packs: nPacks })
  }
  if (nCoins === 1) return t('staffGrant.toastCoin')
  if (nCoins > 1) return t('staffGrant.toastCoins', { n: nCoins })
  if (nPacks === 1) return t('staffGrant.toastPack')
  if (nPacks > 1) return t('staffGrant.toastPacks', { n: nPacks })
  return ''
})
let hideTimer: ReturnType<typeof setTimeout> | null = null

function clearTimer(): void {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

watch(
  [coins, packs],
  ([nCoins, nPacks]) => {
    clearTimer()
    if (nCoins <= 0 && nPacks <= 0) return
    void ackStaffGrantNotice()
    hideTimer = setTimeout(() => {
      staffGrantNoticeCoins.value = 0
      staffGrantNoticePacks.value = 0
    }, TOAST_MS)
  },
  { immediate: true },
)

onUnmounted(() => {
  clearTimer()
})
</script>

<style scoped>
.staff-grant-toast {
  position: fixed;
  left: 50%;
  bottom: calc(224px + env(safe-area-inset-bottom, 0px));
  z-index: 10050;
  display: flex;
  align-items: center;
  gap: 12px;
  width: min(520px, calc(100vw - 32px));
  margin: 0;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  box-shadow: var(--md-elevation-2);
  transform: translateX(-50%);
  font-family: Roboto, system-ui, sans-serif;
}

.copy {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.staff-grant-toast-enter-active,
.staff-grant-toast-leave-active {
  transition:
    opacity var(--md-motion-short) var(--md-motion-standard),
    transform var(--md-motion-short) var(--md-motion-standard);
}

.staff-grant-toast-enter-from,
.staff-grant-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .staff-grant-toast-enter-active,
  .staff-grant-toast-leave-active {
    transition: none;
  }
}
</style>
